import { IRADEnvironmentProxy } from '@/Communication/RADEnvironmentProxy';
import { IRADApplication } from '@/Management/RADApplication';
import ComponentManager from '@/Management/ComponentManager';
import { INavigationService } from '@/Management/NavigationService';
import * as DateHelper from '@/Utility/DateHelper';
import StorageHelper from '@/Utility/StorageHelper';
import MessageBoxViewModelType from '@/ViewModels/Controls/MessageBoxViewModel';
import type { IKeepAliveDTO, INotificationDTO } from "coreframetypings/dto";
import { inject, singleton } from "tsyringe";
import { IKeepAliveHandler } from "@/Management/KeepAliveHandler";

declare let moment: moment.MomentStatic;

@singleton()
export class KeepAliveHandlerImpl implements IKeepAliveHandler {

    /**
     * Mindest-Intervall zum Einblenden der Logout-Aufforderungen
     * 5 Minuten
     */
    private FORCE_LOGOUT_NOTIFICATION_INTERVAL;

    /**
     * Warte-Intervall für den initialen Keep-Alive-Request
     * 5 Sekunden
     */
    private INITIAL_KEEP_ALIVE;

    /**
     * Interval for idle check
     */
    private IDLE_DETECTION_INTERVAL: number;

    /**
     * Interval for initial idlecheck
     */
    private INITIAL_IDLE_DETECTION;

    /**
     * Standard-Intervall für Keep-Alive-Requests. Wird ggf. mit dem Wert der SystemProperty Gui.KeepAliveTime überschrieben
     * 2 Minuten
     */
    private KEEP_ALIVE_INTERVAL;

    /**
     * Standard-Intervall für Logout-Fenster bei erkanntem Idle oder bevorstehenden Systemsperrren
     * 5 Minuten
     */
    private IDLE_TIMESPAN;

    /**
     * Standard-Intervall für die maximale Idle-Zeit bevor eine Logout-Aufforderung erscheint
     * 60 Minuten
     */
    private MAX_IDLE_TIME;
    private SESSION_VALID_TIME;

    /**
     * Jetzt
     */
    private now;

    /**
     * Timer for keep alive
     */
    private keepAliveTimer: number;

    /**
     * Timer for idle detection
     */
    private idleDetectionTimer: number;

    /**
     * Ein Timer zum automatischen Abmelden
     */
    private autoLogoutTimer;

    /**
     * Ein Timer zum forcierten Abmelden
     */
    private forceLogoutTimer;

    /**
     * Zeitspanne für Antwortzeit vom Server
     */
    private clientServerDuration;

    /**
     * Zustand für Abwesenheit
     */
    private idleDetected;

    /**
     * Zustand für System-Lock
     */
    private systemLockDetected;

    /**
     * Erlaub das Deaktivieren des Auto-Logut nach Idle aus außerhalb. Kann zum Beispiel vorlängeren CallOperations aktiviert werden.
     */
    disableAutoLogout: boolean;

    /**
     * Subscription für ClientActions
     */
    private clientActionSubscription;

    /**
     * Zeitpunkt des geplanten automatischen Logouts (wegen Idle), wenn dieser nicht unterbrochen wird
     */
    private scheduledLogoutTime;

    /**
     * Zeitpunkt des geplanten erzwungenen Logouts aufgrund einer Systemsperre. Kann nicht unterbrochen werden
     */
    private systemLockStartTime;

    /**
     * Zeitpunkt an dem zuletzt eine Logout-Warnung eingeblendet wurde. Ist erforderlich, damit das Fenster nicht ständig erneut eingeblendet wird
     */
    private lastForceLogoutNotification;

    /**
     * Zeitpunkt an dem zuletzt eine Aktion im Client ausgeführt wurde
     */
    private lastClientAction;

    /**
     * Zeitpunkt an dem zuletzt ein KeepAlive gesendet wurde
     */
    private lastKeepAliveUtcSendTime;

    /**
     * Zeitpunkt an dem zuletzt ein KeepAlive empfangen wurde
     */
    private lastKeepAliveUtcReceiveTime;

    private logoutNotificationCreated;
    private logoutNotificationViewModel;

    private forceLogoutNotificationCreated;
    private forceLogoutNotificationViewModel;
    
    constructor(
        @inject("RADApplication") private app: IRADApplication,
        @inject("NavigationService") private navigationService: INavigationService,
        @inject("RADEnvironmentProxy") private proxy: IRADEnvironmentProxy,
    ) {
        this.IDLE_DETECTION_INTERVAL = 1000 * 30;
        this.FORCE_LOGOUT_NOTIFICATION_INTERVAL = 1000 * 60 * 5;
        this.INITIAL_KEEP_ALIVE = 1000 * 5;
        this.INITIAL_IDLE_DETECTION = 1000 * 5;
        this.KEEP_ALIVE_INTERVAL = 1000 * 60 * 2;
        this.IDLE_TIMESPAN = 1000 * 60 * 5;
        this.MAX_IDLE_TIME = 1000 * 60 * 60;
        this.SESSION_VALID_TIME = 1000 * 60 * 20;
        this.now = () => { return moment.utc(); };
        this.keepAliveTimer = null;
        this.idleDetectionTimer = null;
        this.autoLogoutTimer = null;
        this.forceLogoutTimer = null;
        this.clientServerDuration = null;
        this.idleDetected = false;
        this.systemLockDetected = false;
        this.disableAutoLogout = false;
        this.clientActionSubscription = null;
        this.scheduledLogoutTime = null;
        this.systemLockStartTime = null;
        this.lastForceLogoutNotification = null;
        this.lastClientAction = null;
        this.lastKeepAliveUtcSendTime = null;
        this.lastKeepAliveUtcReceiveTime = null;

        this.logoutNotificationCreated = false;
        this.forceLogoutNotificationCreated = false;

        this.subscribeForLogout();
    }

    /**
     * Stops watch dog (idle detection, keep alive, etc.) if logout is forced
     */
    private subscribeForLogout() {
        this.app.isForceLogout.subscribe((isForceLogout) => {
            if (isForceLogout) {
                this.stopWatchDog();
            }
        });
    }

    // ================================== KeepAliveInterval ==================================

    private startKeepAliveInterval() {
        this.keepAliveTimer = window.setTimeout(() => {
            this.keepAliveTimerTick();
            this.keepAliveTimer = window.setInterval(this.keepAliveTimerTick.bind(this), this.KEEP_ALIVE_INTERVAL);
        }, this.INITIAL_KEEP_ALIVE);
    }

    private stopKeepAliveInterval() {
        window.clearInterval(this.keepAliveTimer);
    }

    private keepAliveTimerTick() {
        let difference = this.lastKeepAliveUtcReceiveTime.diff(this.lastKeepAliveUtcSendTime, 'seconds') * 1000;

        if (difference <= this.KEEP_ALIVE_INTERVAL) {
            this.lastKeepAliveUtcSendTime = this.now();

            let clientTime = this.lastKeepAliveUtcSendTime.format();

            this.proxy.keepAlive({
                clientTime: clientTime
            }).then((keepAliveDTO: IKeepAliveDTO) => {
                let utcNow = moment.utc(keepAliveDTO.utcNow);
                let difference = utcNow.diff(this.lastKeepAliveUtcSendTime);

                this.clientServerDuration = moment.duration(difference);
                this.lastKeepAliveUtcReceiveTime = this.now();

                this.updateCache(keepAliveDTO);
            }).fail((status, message) => {
                this.showSessionTimeoutNotification();
            });
        } else {
            this.showSessionTimeoutNotification();
        }
    }

    private startIdleDetectionInterval() {
        this.idleDetectionTimer = window.setTimeout(() => {
            this.idleDetection();
            this.idleDetectionTimer = window.setInterval(this.idleDetection.bind(this), this.IDLE_DETECTION_INTERVAL);
        }, this.INITIAL_IDLE_DETECTION);
    }

    private stopIdleDetectionInterval() {
        window.clearInterval(this.idleDetectionTimer);
    }

    private idleDetection() {
        let idleTime = this.now().diff(this.getLastClientAction());

        if (idleTime > this.MAX_IDLE_TIME) {
            if (this.idleDetected === false && this.forceLogoutTimer == null
                && !this.disableAutoLogout && !this.proxy.hasActiveServerCommunication()) {
                this.startAutoLogoutTimer();
            }
        } else {
            this.stopAutoLogoutTimer();
        }
    }

    private updateCache(keepAliveDTO) {
        let lastReceivedCacheUpdate = this.app.lastCacheUpdate();
        let lastReceivedRightCacheUpdate = this.app.lastRightCacheUpdate();

        if (lastReceivedCacheUpdate < keepAliveDTO.lastCacheUpdate
            || lastReceivedRightCacheUpdate < keepAliveDTO.lastRightCacheUpdate) {

            this.app.updateCache();
            this.app.lastCacheUpdate(keepAliveDTO.lastCacheUpdate);
            this.app.lastRightCacheUpdate(keepAliveDTO.lastRightCacheUpdate);
        }
    }

    notifySystemLock(notification: INotificationDTO) {
        if (this.systemLockDetected === false) {
            this.startForceLogoutTimer(notification);
        }
    }

    notifyNoSystemLock() {
        if (this.forceLogoutTimer != null) {
            this.lastForceLogoutNotification = moment.utc(0);
            this.stopForceLogoutTimer();
        }
    }

    // ================================== AutoLogoutTimer ==================================

    private startAutoLogoutTimer() {
        this.idleDetected = true;

        let scheduledLogoutTime = this.now().add(this.IDLE_TIMESPAN);
        let lastAutoLogoutTimerLog = null;
        this.autoLogoutTimer = window.setInterval(() => {
            let difference = scheduledLogoutTime.diff(this.now());
            let remaining = moment.duration(difference);

            if (!lastAutoLogoutTimerLog || this.now().diff(lastAutoLogoutTimerLog) > 60000) {
                lastAutoLogoutTimerLog = this.now();
            }

            this.showLogoutNotification(remaining);

            if (remaining.asMilliseconds() <= 0) {
                this.stopAutoLogoutTimer();
                this.forceLogout();
            }
        }, 1000);
    }

    private stopAutoLogoutTimer() {
        this.idleDetected = false;

        this.hideLogoutNotification();
        window.clearTimeout(this.autoLogoutTimer);
        this.autoLogoutTimer = null;
    }

    // ================================== ListenClientAction ==================================

    private checkKeepaliveActivity() {
        let difference = this.now().diff(this.lastKeepAliveUtcReceiveTime, 'seconds') * 1000;
        if (difference > this.SESSION_VALID_TIME) {
            this.showSessionTimeoutNotification();
        }
    }

    private startListenClientAction() {
        this.clientActionSubscription = ko.computed(() => {
            let clientAction = this.proxy.clientAction();
            this.setLastClientAction(this.now());

            this.checkKeepaliveActivity();
        });
    }

    private stopListenClientAction() {
        if (this.clientActionSubscription != null) {
            this.clientActionSubscription.dispose();
            this.clientActionSubscription = null;
        }
    }

    // ================================== ForceLogoutTimer ==================================

    private startForceLogoutTimer(systemLockNotificationDTO) {
        if (this.forceLogoutTimer != null) {
            this.stopForceLogoutTimer();
            return;
        }

        if (systemLockNotificationDTO.isUserOnExceptionList === true) {
            this.stopForceLogoutTimer();
            return;
        }

        this.systemLockDetected = true;
        this.systemLockStartTime = moment.utc(systemLockNotificationDTO.lockStart);
        this.stopAutoLogoutTimer();

        this.forceLogoutTimer = window.setInterval(() => {
            let difference = this.systemLockStartTime.diff(this.now());
            let remaining = moment.duration(difference);

            if (systemLockNotificationDTO.preNotification === true) {
                this.showForceLogoutNotification(remaining, systemLockNotificationDTO);
            }

            if (remaining.asMilliseconds() <= 0) {
                this.stopForceLogoutTimer();
                this.forceLogout();
            }
        }, 1000);
    }

    private stopForceLogoutTimer() {
        this.systemLockDetected = false;

        this.hideForceLogoutNotification();
        window.clearInterval(this.forceLogoutTimer);
        this.forceLogoutTimer = null;
    }

    // ================================== SessionTimeoutNotification ==================================

    private showSessionTimeoutNotification() {
        this.app.unauthorizedRequestCallback();
    }

    // ================================== LogoutNotification ==================================

    private createLogoutNotification() {
        if (this.logoutNotificationCreated === false) {
            const MessageBoxViewModel = ComponentManager.getViewModel<typeof MessageBoxViewModelType>('MessageBoxViewModel');
            this.logoutNotificationViewModel = MessageBoxViewModel.createLogoutNotificationMessageBox(() => {
                this.setLastClientAction(this.now());
                this.hideForceLogoutNotification();
                this.stopAutoLogoutTimer();
            });

            this.app.showMessageBox(this.logoutNotificationViewModel);
            this.logoutNotificationCreated = true;
        }
    }

    private showLogoutNotification(remaining) {
        this.createLogoutNotification();

        let headlinePostfix = ' (' + this.pad(remaining.minutes(), '0', 2) + ':' + this.pad(remaining.seconds(), '0', 2) + ')';

        let textrep = {
            minuten: remaining.minutes() > 0 ? remaining.minutes() : 1
        };

        if (this.logoutNotificationViewModel.isDisposed === true) {
            this.logoutNotificationCreated = false;
            return;
        }

        this.logoutNotificationViewModel.window.headline.postfix.caption(headlinePostfix);
        this.logoutNotificationViewModel.label.replacements = textrep;
        this.logoutNotificationViewModel.label.ergName.valueHasMutated();
        if (!this.logoutNotificationViewModel.window.isOpen()) {
            this.logoutNotificationViewModel.window.isOpen(true);
        }
    }

    private hideLogoutNotification() {
        if (this.logoutNotificationCreated === true) {
            if (this.logoutNotificationViewModel.isDisposed === false) {
                this.logoutNotificationViewModel.window.isOpen(false);
            } else {
                this.logoutNotificationCreated = false;
            }
        }
    }

    // ================================== ForceLogoutNotification ==================================
    private createForceLogoutNotification() {
        if (this.forceLogoutNotificationCreated === false) {
            const MessageBoxViewModel = ComponentManager.getViewModel<typeof MessageBoxViewModelType>('MessageBoxViewModel');
            this.forceLogoutNotificationViewModel = MessageBoxViewModel.createForceLogoutNotificationMessageBox(() => {
                this.forceLogout();
            }, () => {
                this.setLastClientAction(this.now());
                this.hideForceLogoutNotification();
                this.stopForceLogoutTimer();
            }) as any;

            this.app.showMessageBox(this.forceLogoutNotificationViewModel);
            this.forceLogoutNotificationCreated = true;
        }
    }

    private showForceLogoutNotification(remaining, systemLockNotificationDTO) {
        this.createForceLogoutNotification();

        let headline = ` (${this.pad(remaining.minutes(), '0', 2)}:${this.pad(remaining.seconds(), '0', 2)})`;

        let textrep = {
            minuten: remaining.minutes() > 0 ? remaining.minutes() : 1,
            lockTime: DateHelper.toString(moment.utc(systemLockNotificationDTO.lockStart).toDate(), true),
            lockReason: systemLockNotificationDTO.notificationMessage
        };


        if (this.forceLogoutNotificationViewModel.isDisposed === true) {
            this.forceLogoutNotificationCreated = false;
            return;
        }

        this.forceLogoutNotificationViewModel.window.headline.postfix.caption(headline);
        this.forceLogoutNotificationViewModel.label.replacements = textrep;
        this.forceLogoutNotificationViewModel.label.ergName.valueHasMutated();

        let timeTillForceLogoutNotification = this.now().diff(this.lastForceLogoutNotification);
        let isOpen = this.forceLogoutNotificationViewModel.window.isOpen();

        if (isOpen === false && timeTillForceLogoutNotification > this.FORCE_LOGOUT_NOTIFICATION_INTERVAL) {
            this.forceLogoutNotificationViewModel.window.isOpen(true);
        }
    }

    private hideForceLogoutNotification() {
        if (this.forceLogoutNotificationCreated === true) {
            if (this.forceLogoutNotificationViewModel.isDisposed === false) {
                this.forceLogoutNotificationViewModel.window.isOpen(false);
            } else {
                this.forceLogoutNotificationCreated = false;
            }
        }

        this.lastForceLogoutNotification = this.now();
    }

    // ================================== UserNotification ==================================

 

    // ======================================================================================

    private pad(number, char, count) {
        let result = number + '';

        while (result.length < count) {
            result = char + result;
        }

        return result;
    }

    private reset() {
        this.keepAliveTimer = null;
        this.autoLogoutTimer = null;
        this.forceLogoutTimer = null;
        this.clientServerDuration = null;
        this.idleDetected = false;
        this.systemLockDetected = false;
        this.disableAutoLogout = false;
        this.clientActionSubscription = null;

        this.scheduledLogoutTime = this.now();
        this.systemLockStartTime = this.now();
        this.lastForceLogoutNotification = moment.utc(0);
        this.setLastClientAction(this.now());
        this.lastKeepAliveUtcSendTime = this.now();
        this.lastKeepAliveUtcReceiveTime = this.now();

        this.KEEP_ALIVE_INTERVAL = 1000 * 60 * this.app.getSystemParam('Gui.KeepAliveTime', parseInt);
        this.IDLE_TIMESPAN = 1000 * 60 * this.app.getSystemParam('Gui.IdleWarningTimeout', parseInt);
        this.MAX_IDLE_TIME = 1000 * 60 * this.app.getSystemParam('Gui.MaxIdleTime', parseInt);
        this.SESSION_VALID_TIME = 1000 * 60 * this.app.getSystemParam('Gui.SessionValidTime', parseInt);
    }

    private forceLogout() {
        this.app.isForceLogout(true);
        this.navigationService.navigateToLogout();
    }

    startWatchDog() {
        this.reset();
        this.startListenClientAction();
        this.startKeepAliveInterval();
        this.startIdleDetectionInterval();
    }

    stopWatchDog() {
        this.stopListenClientAction();
        this.stopKeepAliveInterval();
        this.stopIdleDetectionInterval();
    }

    private getLastClientAction() {
        if (this.app.isSystemParamSetAndTrue('Gui.UseAutoLogin')) {
            return StorageHelper.get('KeepAliveHandler.lastClientAction');
        } else
            return this.lastClientAction;
    }

    private setLastClientAction(newValue: any) {
        if (this.app.isSystemParamSetAndTrue('Gui.UseAutoLogin')) {
            StorageHelper.set('KeepAliveHandler.lastClientAction', this.now(), false);
        } else
            this.lastClientAction = newValue;
    }
}